<template>
  <!-- Record mode: existing document -->
  <template v-if="documentId">
    <DocumentRecordCard
      :document-name="adapter?.documentName"
      :lifecycle-status="document?.lifecycle_status"
      :days-before-expiration="document?.days_before_expiration"
      :expiration-date="document?.expiration_date"
      :internal-reviews="document?.internal_reviews"
      :external-reviews="document?.external_reviews"
      :is-loading="isLoading"
      :error="error"
      :document-type="documentType"
      :allowed-document-types="allowedDocumentTypes"
      :allow-delete="allowDelete"
      :replaced="isReplaced"
      :readonly="readonly"
      @click="handleCardClick"
      @verify="handleCardClick"
      @replace="handleReplace"
      @remove="handleRemove"
      @download="handleDownload"
    />

    <!-- Document Record Dialog (for verified documents) -->
    <DocumentRecordDialog
      v-if="adapter && shouldOpenRecordDialog"
      v-bind="documentStatusBadgeProps"
      :open="recordDialogOpen"
      :title="adapter.documentName"
      :files="recordDialogFiles"
      :form-fields="adapter.formFields"
      :data="recordDialogData"
      :is-loading="isRecordDialogLoading"
      :document-type="documentType"
      :allowed-document-types="allowedDocumentTypes"
      :allow-delete="allowDelete"
      :replaced="isReplaced"
      @update:open="recordDialogOpen = $event"
      @request-close="recordDialogOpen = false"
      @download="handleDownload"
      @replace="handleRecordDialogReplace"
      @delete="handleRemove"
    />

    <!-- Verification Dialog (for unverified documents + replace flow) -->
    <DocumentVerificationDialog
      v-if="activeAdapter"
      ref="verificationDialogRef"
      v-model:open="showVerificationDialog"
      v-bind="documentStatusBadgeProps"
      :title="activeAdapter.documentName"
      :files="files"
      :max-files="activeAdapter.maxFiles"
      :form-fields="activeAdapter.formFields"
      :adapter="activeAdapter"
      :initial-data="extractedData"
      :is-loading="isExtracting"
      :is-storing="isStoring"
      :is-rejecting="isRejecting"
      :extraction-error="extractionError"
      :file-limit-warning="fileLimitWarning"
      :file-mime-type-warning="fileMimeTypeWarning"
      :context="context ?? {}"
      :show-verification-banner="showVerificationBanner"
      @complete="handleVerificationComplete"
      @reject="handleVerificationReject"
      @cancel="resetFlow"
      @request-close="handleVerificationRequestClose"
      @add-files-requested="awFileUploadRef?.openFileDialog()"
      @remove-file="handleRemoveFile"
      @dismiss-file-limit-warning="dismissFileLimitWarning"
      @dismiss-file-mime-type-warning="dismissFileMimeTypeWarning"
    />

    <!-- Rejection Dialog (asks for rejection reason) -->
    <DocumentRejectionDialog
      :open="showRejectionDialog"
      :is-rejecting="isRejecting"
      @update:open="if (!$event) handleRejectionCancel();"
      @cancel="handleRejectionCancel"
      @confirm="handleRejectionConfirm"
    />

    <!-- Hidden file upload for replace/add-files flows -->
    <AwFileUpload
      v-if="activeAdapter"
      ref="awFileUploadRef"
      class="!hidden"
      :files="[]"
      :accept="activeAdapter.acceptedMimeTypes"
      :max-files="activeAdapter.maxFiles"
      @update:files="handleNewFiles"
    />

    <!-- Confirmation Dialog (abandon verification) -->
    <AwModal v-model:open="showConfirmDialog" :title="$t('documents.item.confirmAbandon.title')" size="md">
      <template #body>
        <div class="p-4">{{ $t("documents.item.confirmAbandon.description") }}</div>
      </template>

      <template #footer>
        <AwButton color="secondary" variant="outline" @click="handleCancelClose">{{
          $t("documents.item.confirmAbandon.continue")
        }}</AwButton>
        <AwButton color="secondary" @click="handleConfirmClose">{{
          $t("documents.item.confirmAbandon.abandon")
        }}</AwButton>
      </template>
    </AwModal>

    <!-- Delete Confirmation Dialog -->
    <ConfirmModal
      v-if="allowDelete"
      :is-open="showDeleteConfirmDialog"
      :title="$t('documents.item.confirmDelete.title')"
      :description="$t('documents.item.confirmDelete.description')"
      :primary-label="$t('documents.item.confirmDelete.confirm')"
      :is-confirming="isDeleting"
      @update:open="showDeleteConfirmDialog = $event"
      @update:on-confirm="handleDeleteConfirm"
    />
  </template>

  <!-- Upload mode -->
  <template v-else>
    <div class="flex flex-col gap-2">
      <!-- Category dropdown (when multiple types allowed) -->
      <AwSelectMenu
        v-if="isCategoryMode"
        v-model="selectedType"
        :items="typeOptions"
        :placeholder="$t('documents.item.typePlaceholder')"
        label-key="label"
        value-key="value"
        :search-input="false"
      />

      <!-- Upload input (shown when type is resolved) -->
      <DocumentUploadInput
        v-if="resolvedType"
        :document-type="resolvedType"
        :context="context ?? {}"
        :disabled="disabled"
        @uploaded="emit('update:documentId', $event)"
      />
    </div>
  </template>
</template>

<script setup lang="ts">
import { useApi } from "~/composables/useApi";
import { useProtectedRoute } from "~/composables/useProtectedRoute";
import { useToast } from "~/composables/useToast";
import { AwFileUpload } from "@asap/design";
import { getHandler } from "./handlers/registry";
import type { AnyDocumentTypeHandler } from "./handlers/handler";
import type { DocumentType } from "./handlers/types";
import { useDocumentUpload } from "./upload/useDocumentUpload";
import { useDocumentRecord } from "./record/useDocumentRecord";
import DocumentRecordCard from "./record/DocumentRecordCard.vue";
import DocumentRecordDialog from "./record/DocumentRecordDialog.vue";
import DocumentUploadInput from "./upload/DocumentUploadInput.vue";
import DocumentVerificationDialog from "./upload/DocumentVerificationDialog.vue";
import DocumentRejectionDialog from "./upload/DocumentRejectionDialog.vue";
import ConfirmModal from "~/components/shared/modal/ConfirmModal.vue";
import { getDialogToOpen } from "./record/getDialogToOpen";
import type { Props as DocumentStatusBadgeProps } from "./record/DocumentStatusBadge.vue";

interface Props {
  // Record mode (existing document)
  documentId?: string;

  // Upload mode - single type
  documentType?: DocumentType;

  // Upload mode - category (user picks from list)
  allowedDocumentTypes?: DocumentType[];

  // Common
  context?: Record<string, unknown>;
  disabled?: boolean;
  allowDelete?: boolean;

  // Shows the document and its status only: no verify, replace or delete, and a click emits `preview`
  // instead of opening a dialog.
  readonly?: boolean;

  // When true, still surfaces the document if it has been soft-deleted via replacement
  // (e.g. a card request still pointing at a since-replaced temporary BTP card).
  // The document renders read-only with an "Archivé" badge.
  showWhenReplaced?: boolean;

  // Extra values shown in the record dialog. The card does not know what they mean.
  recordData?: Record<string, string>;
}

interface Emits {
  "update:documentId": [documentId: string | undefined];
  "record-open": [];
  preview: [];
}

const props = withDefaults(defineProps<Props>(), {
  documentId: undefined,
  documentType: undefined,
  allowedDocumentTypes: undefined,
  context: () => ({}),
  allowDelete: true,
  readonly: false,
  showWhenReplaced: false,
  recordData: undefined,
});
const emit = defineEmits<Emits>();

// =============================================================================
// Upload mode state
// =============================================================================

// Category mode: multiple types to choose from
const isCategoryMode = computed(
  () => !props.documentId && props.allowedDocumentTypes && props.allowedDocumentTypes.length
);

// Selected type in category mode
const selectedType = ref<DocumentType | undefined>();

// Type options for dropdown
const typeOptions = computed(() => {
  if (!props.allowedDocumentTypes) return [];
  return props.allowedDocumentTypes.map((type) => ({
    value: type,
    label: getHandler(type).documentName,
  }));
});

// Resolved type for upload input
const resolvedType = computed<DocumentType | undefined>(() => {
  if (props.documentType) return props.documentType;
  return selectedType.value;
});

// =============================================================================
// Record mode state
// =============================================================================

// Document ID as computed ref for composable
const { documentId: documentIdRef, showWhenReplaced: showWhenReplacedRef } = toRefs(props);

// Use the document record composable
const { document, adapter, isLoading, error, fetchFiles, refetch } = useDocumentRecord({
  documentId: documentIdRef,
  includeReplaced: showWhenReplacedRef,
});

const isReplaced = computed(() => Boolean(document.value?.replaced_by_document_id));

// Replaced documents are read-only artifacts — bypass the verification path even
// if the (now stale) lifecycle status would otherwise route there.
const dialogToOpen = computed(() => {
  if (isReplaced.value) return "record";
  return getDialogToOpen(document.value?.lifecycle_status);
});

const shouldOpenRecordDialog = computed(() => dialogToOpen.value === "record");
const shouldOpenVerificationDialog = computed(() => dialogToOpen.value === "verification");

// Record dialog state (for verified documents)
const recordDialogOpen = ref(false);
const recordDialogFiles = ref<File[]>([]);
const isRecordDialogLoading = ref(false);

const recordDialogData = computed(() => {
  const storedData = document.value?.data;
  if (!props.recordData) return storedData ?? undefined;
  return { ...(storedData ?? {}), ...props.recordData };
});

// Replace flow state
const replaceType = ref<DocumentType | null>(null);
const isReplacingDocument = computed(() => Boolean(replaceType.value));

// Badge props are only relevant when verifying an existing document, not when replacing it
const documentStatusBadgeProps = computed<DocumentStatusBadgeProps>(() => {
  if (isReplacingDocument.value) return {};
  return {
    lifecycleStatus: document.value?.lifecycle_status,
    daysBeforeExpiration: document.value?.days_before_expiration,
    expirationDate: document.value?.expiration_date,
    internalReviews: document.value?.internal_reviews,
    externalReviews: document.value?.external_reviews,
  };
});

// Active adapter: switches between document adapter (verification) and replace adapter
const activeAdapter = computed<AnyDocumentTypeHandler | null>(() => {
  // If we're in replace mode, use the replace handler
  if (replaceType.value) return getHandler(replaceType.value);
  // Otherwise use the document's adapter (for verification flow)
  return adapter.value;
});

// A document is uploaded by a talent when its creator has no team_member row
const isUploadedByTalent = computed(() => {
  return Boolean(document.value?.creator && document.value.creator.team_member_id === null);
});

// Show verification banner when a team member is verifying a document uploaded by a talent
const showVerificationBanner = computed(() => {
  return Boolean(
    props.documentId && shouldOpenVerificationDialog.value && !isReplacingDocument.value && isUploadedByTalent.value
  );
});

// Context as computed ref for composable
const { context } = toRefs(props);

// AwFileUpload template ref for programmatic file dialog
const awFileUploadRef = useTemplateRef("awFileUploadRef");

// Use a single upload composable for both verification and replace flows
const {
  files,
  fileLimitWarning,
  fileMimeTypeWarning,
  dismissFileLimitWarning,
  dismissFileMimeTypeWarning,
  extractedData,
  extractionError,
  isExtracting,
  showVerificationDialog,
  verificationDialogRef,
  isStoring,
  isRejecting,
  showRejectionDialog,
  handleNewFiles,
  handleRemoveFile,
  handleVerificationComplete,
  handleVerificationReject,
  handleRejectionConfirm,
  handleRejectionCancel,
  handleVerificationRequestClose,
  showConfirmDialog,
  handleConfirmClose,
  handleCancelClose,
  resetFlow,
  openVerificationDialog,
  setDocumentToReplace,
} = useDocumentUpload({
  adapter: activeAdapter,
  context,
  onUploaded: (documentId) => emit("update:documentId", documentId),
  onVerified: (documentId) => emit("update:documentId", documentId),
  onRejected: (documentId) => {
    emit("update:documentId", documentId);
    refetch();
  },
});

// =============================================================================
// API and toast
// =============================================================================

const config = useRuntimeConfig();
const { customFetch } = useProtectedRoute();
const { invoke } = useApi();
const { t } = useI18n();
const { addToastSuccess, addToastError } = useToast();

// =============================================================================
// Download
// =============================================================================

async function handleDownload() {
  if (!props.documentId) return;

  const accessToken = useCookie("sb-access-token").value;
  if (!accessToken) {
    addToastError({ description: t("documents.item.authError") });
    return;
  }

  try {
    const response = await customFetch(`${config.public.API_URL}/document/${props.documentId}/download`, {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    // Get filename from Content-Disposition header
    const header = response.headers.get("Content-Disposition");
    const filename = header
      ? decodeURIComponent(header.replace("attachment; filename=", "").replace(/"/g, ""))
      : "document";

    // Download the file
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    addToastError({ description: t("documents.item.downloadError") }, error);
  }
}

// =============================================================================
// Delete state
// =============================================================================

const showDeleteConfirmDialog = ref(false);
const isDeleting = ref(false);

async function handleDeleteConfirm() {
  if (!props.documentId) return;

  isDeleting.value = true;
  try {
    await invoke({ path: "document/:id", method: "DELETE", params: { id: props.documentId } });
    showDeleteConfirmDialog.value = false;
    addToastSuccess({ description: t("documents.item.deleted") });
    emit("update:documentId", undefined);
  } catch (error) {
    addToastError({ description: t("documents.item.deleteError") }, error);
  } finally {
    isDeleting.value = false;
  }
}

// =============================================================================
// Event handlers
// =============================================================================

async function handleCardClick() {
  if (props.readonly) {
    emit("preview");
    return;
  }
  if (!document.value || !adapter.value) return;

  if (dialogToOpen.value === "record") {
    // Open readonly record dialog for verified documents
    recordDialogOpen.value = true;
    isRecordDialogLoading.value = true;
    recordDialogFiles.value = [];
    emit("record-open");

    try {
      recordDialogFiles.value = await fetchFiles();
    } catch {
      recordDialogOpen.value = false;
    } finally {
      isRecordDialogLoading.value = false;
    }
  } else if (dialogToOpen.value === "verification") {
    // Open verification dialog for unverified documents
    try {
      const fetchedFiles = await fetchFiles();
      // For failed documents, data contains the validated values from internal review
      // For unverified documents, fall back to extracted_data (form-friendly format from initial upload)
      const initialData = document.value.data ?? document.value.extracted_data ?? {};
      // Pass document ID so the backend can replace the old document
      openVerificationDialog(fetchedFiles, initialData, document.value.id);
    } catch (err) {
      console.error("Failed to open verification dialog:", err);
    }
  }
}

function handleReplace(type: DocumentType) {
  replaceType.value = type;
  // Set the document ID to replace before user selects new files
  if (props.documentId) {
    setDocumentToReplace(props.documentId);
  }
  // Wait for next tick so activeAdapter is updated before opening file dialog
  nextTick(() => {
    awFileUploadRef.value?.openFileDialog();
  });
}

function handleRecordDialogReplace(type: DocumentType) {
  // Close the record dialog before starting replace flow
  recordDialogOpen.value = false;
  handleReplace(type);
}

function handleRemove() {
  if (!props.allowDelete) return;
  showDeleteConfirmDialog.value = true;
}
</script>
